"use client";

import { type Listing } from '@/services/api';
import { apiClient } from '@/services/api/client';
import { EVENT_TYPES } from '@/utils/constants';
import { logEvent } from '@/utils/helpers/analyticsHelpers';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

interface FavoritesContextType {
  favoritedListings: Set<Listing>;
  setFavoritedListings: React.Dispatch<React.SetStateAction<Set<Listing>>>;
  addFavorite: (listing: Listing) => Promise<boolean>;
  removeFavorite: (listingId: string) => Promise<boolean>;
  determineIfFavorited: (listingId: string) => boolean;
  loading: boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider = ({ children }: { children: React.ReactNode }) => {
  const [favoritedListings, setFavoritedListings] = useState<Set<Listing>>(new Set());
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchFavoritedItems = React.useCallback(async () => {
    if (!user?._id) {
      setFavoritedListings(new Set());
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get<{ listings: Listing[] }>(`/users/${user._id}/listings/favorites`);
      setFavoritedListings(new Set(response.listings));
    } catch {
      setFavoritedListings(new Set());
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchFavoritedItems();
  }, [fetchFavoritedItems]);

  const addFavorite = async (listing: Listing): Promise<boolean> => {
    if (!user?._id) return false;

    try {
      const response = await apiClient.post<{ status: string }>(`/users/${user._id}/listings/favorites`, {
        listing_id: listing._id
      });

      if (response.status === "ok") {
        setFavoritedListings(prev => new Set(prev).add(listing));

        logEvent(user, EVENT_TYPES.FAVORITE_ADDED, {
          listing_id: listing._id,
          vendor: listing.vendor
        });

        return true;
      }

      return response.status !== "already favorited";
    } catch (error) {
      console.error('Error adding favorite:', error);
      return false;
    }
  };

  const removeFavorite = async (listingId: string): Promise<boolean> => {
    if (!user?._id) return false;

    try {
      const response = await apiClient.delete<{ status: string }>(`/users/${user._id}/listings/favorites/${listingId}`);

      if (response.status === "ok") {
        let removedListing: Listing | undefined;

        setFavoritedListings(prev => {
          const newSet = new Set(prev);
          removedListing = Array.from(newSet).find(item => item._id === listingId);
          if (removedListing) {
            newSet.delete(removedListing);
          }
          return newSet;
        });

        if (removedListing) {
          logEvent(user, EVENT_TYPES.FAVORITE_REMOVED, {
            listing_id: listingId,
            vendor: removedListing.vendor
          });
        }

        return true;
      }

      return response.status !== "not found in favorites";
    } catch (error) {
      console.error('Error removing favorite:', error);
      return false;
    }
  };

  const determineIfFavorited = (listingId: string): boolean => {
    if (!listingId || !user?._id) return false;
    return Array.from(favoritedListings).some(item => item._id === listingId);
  };

  const value: FavoritesContextType = {
    favoritedListings,
    setFavoritedListings,
    addFavorite,
    removeFavorite,
    determineIfFavorited,
    loading,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
